import { Typography, Table, Tooltip, Grid } from "antd";
import { ColumnsType } from "antd/es/table";
import { ColumnFilterItem } from "antd/es/table/interface";
import { InfoCircleOutlined } from "@ant-design/icons";
import type { VehiclesMeta } from "../../helpers/interfaces/VehiclesInterfaces";

export interface PriceSnapshot {
  date: string;
  discounted_cash_price: number | null;
  target_buying: number | null;
  gdfi_fmv: number | null;
  gdfi_fin_p: number | null;
  tfs_fin_p: number | null;
  pre_order_cash_price: number | null;
  reserve_fin_p: number | null;
}

export interface FmvRecord {
  key?: string;
  vehicle_id?: number;
  brand: string;
  model: string;
  trim: string;
  model_year: number;
  transmission: string;
  location: string;
  BMTYTL: string;
  // Base SRP for the TFS v2 loan cap (vehicles.tfs_srp) — vehicle-level, not per snapshot
  tfs_srp?: number | null;
  discounted_cash_price: number | null;
  target_buying: number | null;
  gdfi_fmv: number | null;
  gdfi_fin_p: number | null;
  tfs_fin_p: number | null;
  pre_order_cash_price: number | null;
  reserve_fin_p: number | null;
  snapshots?: PriceSnapshot[];
}

export interface FmvAPI {
  key?: string;
  vehicle_id?: number;
  brand: string;
  model: string;
  trim: string;
  model_year: number;
  transmission: string;
  location: string;
  bmtytl_code: string;
  discounted_cash_price: number | null;
  target_buying: number | null;
  gdfi_fmv: number | null;
  gdfi_fin_p: number | null;
  tfs_fin_p: number | null;
  pre_order_cash_price: number | null;
  reserve_fin_p: number | null;
  snapshots?: PriceSnapshot[];
}

type FmvTableProps = {
  data: FmvRecord[];
  loading: boolean;
  onSelectRow: (record: FmvRecord | null) => void;
  selectedRowKey: string | null;
  meta: VehiclesMeta | null;
  onPageChange: (page: number, pageSize: number) => void;
};

const currencyFormatter = (value: number | null) => {
  if (value === null || value === undefined || isNaN(value)) return "—";
  return `₱ ${value.toLocaleString("en-PH")}`;
};

const dateTimeFormatter = (value: string | null | undefined) => {
  if (!value) return "—";
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat("en-PH", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  } catch {
    return value;
  }
};

const getUniqueFilters = (data: FmvRecord[], field: keyof FmvRecord): ColumnFilterItem[] => {
  const unique = Array.from(new Set(data.map((item) => String(item[field] ?? "")))).filter(Boolean).sort();
  return unique.map((val) => ({ text: val, value: val }));
};

export const FmvTableComponent = ({
  data,
  loading,
  onSelectRow,
  selectedRowKey,
  meta,
  onPageChange,
}: FmvTableProps) => {
  const screens = Grid.useBreakpoint();

  const expandedRowRender = (record: FmvRecord) => {
    const snapshotColumns = [
      { title: "Update Date", dataIndex: "date", key: "date", width: 220, render: dateTimeFormatter },
      { title: "Discounted Cash Price", dataIndex: "discounted_cash_price", key: "discounted_cash_price", render: currencyFormatter, align: "right" as const },
      { title: "Target Buying", dataIndex: "target_buying", key: "target_buying", render: currencyFormatter, align: "right" as const },
      { title: "GDFI FMV", dataIndex: "gdfi_fmv", key: "gdfi_fmv", render: currencyFormatter, align: "right" as const },
      { title: "GDFI Fin Price", dataIndex: "gdfi_fin_p", key: "gdfi_fin_p", render: currencyFormatter, align: "right" as const },
      { title: "TFS Fin Price", dataIndex: "tfs_fin_p", key: "tfs_fin_p", render: currencyFormatter, align: "right" as const },
      { title: "Pre-order Cash", dataIndex: "pre_order_cash_price", key: "pre_order_cash_price", render: currencyFormatter, align: "right" as const },
      { title: "Reserve Fin Price", dataIndex: "reserve_fin_p", key: "reserve_fin_p", render: currencyFormatter, align: "right" as const },
    ];

    return (
      <div style={{ padding: screens.xs ? "8px" : "16px", background: "#fdfdfd", borderRadius: "8px", border: "1px solid #d6dee8", maxWidth: "100%", overflowX: "auto" }}>
        <Typography.Title level={5} style={{ fontSize: "13px", marginBottom: "12px", color: "#1e293b" }}>
          Price Progression History & Comparison
        </Typography.Title>
        <Table
          columns={snapshotColumns}
          dataSource={record.snapshots || []}
          pagination={false}
          size="small"
          bordered
          locale={{ emptyText: "No historical data available." }}
          scroll={{ x: 1200 }}
        />
      </div>
    );
  };

  const columns: ColumnsType<FmvRecord> = [
    // ... same columns as before ...
    {
      title: "Brand",
      dataIndex: "brand",
      key: "brand",
      width: 100,
      filters: getUniqueFilters(data, "brand"),
      onFilter: (value, record) => record.brand === value,
      sorter: (a, b) => a.brand.localeCompare(b.brand),
      fixed: screens.xs ? undefined : ("left" as const),
    },
    {
      title: "Model",
      dataIndex: "model",
      key: "model",
      width: 120,
      filters: getUniqueFilters(data, "model"),
      onFilter: (value, record) => record.model === value,
      sorter: (a, b) => a.model.localeCompare(b.model),
      fixed: screens.xs ? undefined : ("left" as const),
    },
    {
      title: "Trim",
      dataIndex: "trim",
      key: "trim",
      width: 80,
    },
    {
      title: "Year",
      dataIndex: "model_year",
      key: "model_year",
      width: 80,
      filters: getUniqueFilters(data, "model_year"),
      onFilter: (value, record) => String(record.model_year) === value,
      sorter: (a, b) => a.model_year - b.model_year,
    },
    {
      title: "Transmission",
      dataIndex: "transmission",
      key: "transmission",
      width: 110,
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 100,
      filters: getUniqueFilters(data, "location"),
      onFilter: (value, record) => record.location === value,
    },
    {
      title: (
        <Tooltip title="Brand-Model-Trim-Year-Transmission-Location">
          BMTYTL <InfoCircleOutlined style={{ fontSize: 12 }} />
        </Tooltip>
      ),
      dataIndex: "BMTYTL",
      key: "BMTYTL",
      width: 280,
      ellipsis: true,
    },
    {
      title: "Discounted Cash Price",
      dataIndex: "discounted_cash_price",
      key: "discounted_cash_price",
      width: 160,
      render: currencyFormatter,
      sorter: (a, b) => (a.discounted_cash_price ?? 0) - (b.discounted_cash_price ?? 0),
      align: "right",
    },
    {
      title: "Target Buying",
      dataIndex: "target_buying",
      key: "target_buying",
      width: 140,
      render: currencyFormatter,
      sorter: (a, b) => (a.target_buying ?? 0) - (b.target_buying ?? 0),
      align: "right",
    },
    {
      title: "GDFI FMV",
      dataIndex: "gdfi_fmv",
      key: "gdfi_fmv",
      width: 130,
      render: currencyFormatter,
      sorter: (a, b) => (a.gdfi_fmv ?? 0) - (b.gdfi_fmv ?? 0),
      align: "right",
    },
    {
      title: "GDFI Fin Price",
      dataIndex: "gdfi_fin_p",
      key: "gdfi_fin_p",
      width: 130,
      render: currencyFormatter,
      sorter: (a, b) => (a.gdfi_fin_p ?? 0) - (b.gdfi_fin_p ?? 0),
      align: "right",
    },
    {
      title: "TFS Fin Price",
      dataIndex: "tfs_fin_p",
      key: "tfs_fin_p",
      width: 130,
      render: currencyFormatter,
      sorter: (a, b) => (a.tfs_fin_p ?? 0) - (b.tfs_fin_p ?? 0),
      align: "right",
    },
    {
      title: (
        <Tooltip title="TFS base SRP used for the TFS v2 max-loan cap. Blank means the calculator falls back to the ₱20M placeholder.">
          TFS SRP <InfoCircleOutlined style={{ fontSize: 12 }} />
        </Tooltip>
      ),
      dataIndex: "tfs_srp",
      key: "tfs_srp",
      width: 130,
      render: currencyFormatter,
      sorter: (a, b) => (a.tfs_srp ?? 0) - (b.tfs_srp ?? 0),
      align: "right",
    },
    {
      title: "Pre-order Cash Price",
      dataIndex: "pre_order_cash_price",
      key: "pre_order_cash_price",
      width: 160,
      render: currencyFormatter,
      sorter: (a, b) => (a.pre_order_cash_price ?? 0) - (b.pre_order_cash_price ?? 0),
      align: "right",
    },
    {
      title: "Reserve Fin Price",
      dataIndex: "reserve_fin_p",
      key: "reserve_fin_p",
      width: 140,
      render: currencyFormatter,
      sorter: (a, b) => (a.reserve_fin_p ?? 0) - (b.reserve_fin_p ?? 0),
      align: "right",
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={data}
        size="small"
        bordered
        loading={loading}
        scroll={{ x: 1930 }}
        expandable={{
          expandedRowRender,
          rowExpandable: (record) => !!record.snapshots?.length,
        }}
        pagination={{
          current: meta?.current_page ?? 1,
          pageSize: meta?.per_page ?? 15,
          total: meta?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: ["10", "15", "25", "50", "100"],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
          onChange: onPageChange,
        }}
        rowSelection={{
          type: "radio",
          selectedRowKeys: selectedRowKey ? [selectedRowKey] : [],
          onChange: (_keys, selectedRows) => {
            onSelectRow(selectedRows.length > 0 ? selectedRows[0] : null);
          },
        }}
        rowKey="BMTYTL"
        onRow={(record) => ({
          onClick: () => onSelectRow(record),
          style: { cursor: "pointer" },
          className: selectedRowKey === record.BMTYTL ? "selected-row" : "",
        })}
      />
      <style>{`
        .selected-row td {
          background-color: #1e293b !important;
          color: white !important;
        }
        .selected-row .ant-typography {
          color: white !important;
        }
        .selected-row:hover td {
          background-color: #3d416a !important;
        }
      `}</style>
    </>
  );
};
